/**
 * How the playout view writes times (PLAN §7.4): countdowns, over/under and
 * clock times. Whole seconds — an operator reads a countdown at a glance, and
 * tenths flicker.
 *
 * **Countdowns round towards the deadline**: 0.3 s left is `0:01`, never
 * `0:00` while time remains; `0:00` is shown exactly at the deadline, and past
 * it the countdown is an overrun, `+0:01` after a full second over.
 */

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** A non-negative whole-second count as `m:ss` or `h:mm:ss`. */
export function clockOf(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${String(h)}:${pad2(m)}:${pad2(s)}` : `${String(m)}:${pad2(s)}`;
}

export type Countdown = {
  /** `1:05`, `0:00`, or `+0:12` once over. */
  readonly text: string;
  /** Past the deadline. */
  readonly over: boolean;
};

/**
 * A countdown from the ms remaining (negative: over). Null (no expected
 * duration) is `–`.
 */
export function formatCountdown(remainingMs: number | null): Countdown {
  if (remainingMs === null) return { text: '–', over: false };
  if (remainingMs >= 0) return { text: clockOf(Math.ceil(remainingMs / 1000)), over: false };
  return { text: `+${clockOf(Math.floor(-remainingMs / 1000))}`, over: true };
}

/** Elapsed time, rounded down: `0:07` after 7.9 s. */
export function formatElapsed(ms: number): string {
  return clockOf(Math.floor(Math.max(0, ms) / 1000));
}

/** A planned length, rounded to the nearest second; null is `–`. */
export function formatLength(ms: number | null): string {
  return ms === null ? '–' : clockOf(Math.round(Math.max(0, ms) / 1000));
}

export type OverUnder = {
  readonly text: string;
  /** `over`: running long (+); `under`: short (−); `even` within half a second; `none`: nothing to hold it to. */
  readonly tone: 'over' | 'under' | 'even' | 'none';
};

/** Over (+) / under (−), ms → `+1:05`, `−0:30`, `±0:00`; null → `–`. */
export function formatOverUnder(ms: number | null): OverUnder {
  if (ms === null) return { text: '–', tone: 'none' };
  const seconds = Math.round(Math.abs(ms) / 1000);
  if (seconds === 0) return { text: '±0:00', tone: 'even' };
  return ms > 0 ? { text: `+${clockOf(seconds)}`, tone: 'over' } : { text: `−${clockOf(seconds)}`, tone: 'under' };
}

/** A wall-clock time of day in the browser's zone, `HH:MM:SS`; null is `–`. */
export function formatTimeOfDay(epochMs: number | null): string {
  if (epochMs === null) return '–';
  const d = new Date(epochMs);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
